import React, { useMemo } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderData, OrderPageData } from '../api';
import {
  findMultipleTicketsUiObject,
  getTicketBreakdown,
  MultipleTicketsQuantities,
  readMultipleTicketsQuantities,
  TicketBreakdownEntry,
} from '../common/multipleTicketsUtils';
import { useLanguage } from '../common/GeneralUtils';

type BaseProps = {
  orderPage?: OrderPageData;
  variant?: 'compact' | 'stacked';
};
type FromOrderProps = BaseProps & { order: OrderData; quantities?: undefined };
type FromQuantitiesProps = BaseProps & { order?: undefined; quantities: MultipleTicketsQuantities };
type TicketBreakdownDisplayProps = FromOrderProps | FromQuantitiesProps;

export const TicketBreakdownDisplay = ({
  orderPage,
  variant = 'stacked',
  order,
  quantities: quantitiesProp,
}: TicketBreakdownDisplayProps) => {
  const language = useLanguage();
  const { t } = useTranslation();
  const uiObject = useMemo(() => findMultipleTicketsUiObject(orderPage), [orderPage]);
  const customDetails = order?.custom_details;

  const quantities: MultipleTicketsQuantities = useMemo(() => {
    if (quantitiesProp) return quantitiesProp;
    return readMultipleTicketsQuantities(customDetails as Record<string, any> | undefined);
  }, [quantitiesProp, customDetails]);

  const entries = useMemo<TicketBreakdownEntry[]>(() => {
    if (!uiObject) return [];
    return getTicketBreakdown(quantities, uiObject, language);
  }, [quantities, uiObject, language]);

  if (!uiObject || entries.length === 0) return null;

  const LABEL_MAX = 10;
  const truncate = (s: string) => (s.length > LABEL_MAX ? `${s.slice(0, LABEL_MAX)}…` : s);

  const renderEntry = (entry: TicketBreakdownEntry, includeAddOnSuffix: boolean) => {
    const suffix = entry.archived
      ? ` (${t('multipleTicketsUiObject.archivedBadge')})`
      : !entry.counts && includeAddOnSuffix
      ? ` · ${t('multipleTicketsUiObject.addOnBadge')}`
      : '';
    return `${entry.quantity} × ${truncate(entry.label)}${suffix}`;
  };

  if (variant === 'compact') {
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        {entries.map((e) => (
          <Tooltip key={e.ticketId} title={e.label.length > LABEL_MAX ? e.label : ''} arrow>
            <Typography
              variant="caption"
              color={e.archived ? 'text.disabled' : 'text.secondary'}
              sx={{ textDecoration: e.archived ? 'line-through' : undefined }}>
              {renderEntry(e, false)}
            </Typography>
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25} mt={0.25}>
      {entries.map((e) => (
        <Typography
          key={e.ticketId}
          variant="caption"
          sx={{
            color: e.archived ? 'text.disabled' : 'text.secondary',
            textDecoration: e.archived ? 'line-through' : undefined,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
          <Tooltip title={e.label.length > LABEL_MAX ? e.label : ''} arrow>
            <span>
              {e.quantity} × {truncate(e.label)}
            </span>
          </Tooltip>
          {!e.counts && !e.archived && (
            <Chip label={t('multipleTicketsUiObject.addOnBadge')} size="small" variant="outlined" />
          )}
          {e.archived && <Chip label={t('multipleTicketsUiObject.archivedBadge')} size="small" variant="outlined" />}
        </Typography>
      ))}
    </Stack>
  );
};
